import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import HouseIcon from "assets/icons/ic_map_type_used.svg";
import { calcOld } from "common/functions/estates";
import { toHalfWidth } from "common/functions/utilities";
import { MinimalEstate } from "common/queries/minimalEstates";
import { Estate } from "graphql/API";
import React, { useCallback, useMemo, useState } from "react";
import styles from "../PricingDialog.module.scss";
import {
  dspCompanyName,
  dspEstatePrice,
  dspFullAddress,
  dspSellingDays,
  dspStatus,
  dspTsuboArea,
  dspUpdatedAt,
  sellingDaysForCompare,
  updateAtForCompare
} from "./LandTable";
import { SortableColumn } from "./SortableColumn";

export const layoutForCompare = (estate: Estate) => {
  const layoutNumber = Number(
    toHalfWidth(
      estate.layout?.match(/ワンルーム/g)
        ? "1"
        : estate.layout?.match(/^[0-9０-９]+/g)?.[0] || "X"
    )
  );

  if (isNaN(layoutNumber)) {
    return 0;
  }

  return layoutNumber;
};

export const dspLayout = (estate: Estate) => (estate.layout ?? "") || "-";

type ColumnName =
  | "status"
  | "fullAddress"
  | "companyName"
  | "old"
  | "estatePrice"
  | "tsuboArea"
  | "estateType"
  | "layout"
  | "updatedAt"
  | "sellingDays";

export const HouseTable = React.memo((props: { allHouseEstates: Estate[] }) => {
  const [sortColumn, setSortColumn] = useState<ColumnName | undefined>(
    undefined
  );
  const [sortDirection, setSortDirection] = useState<
    "asc" | "desc" | undefined
  >(undefined);

  const onSortClick = useCallback(
    (column: ColumnName, sortDirection: "asc" | "desc") => {
      setSortColumn(column);
      setSortDirection(sortDirection);
    },
    []
  );

  const sortedAllHouseEstates = useMemo(() => {
    return props.allHouseEstates.toSorted((a, b) => {
      if (sortColumn === "status") {
        return (
          (dspStatus(a.status) ?? "").localeCompare(dspStatus(b.status) ?? "") *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "fullAddress") {
        return (
          dspFullAddress(a).localeCompare(dspFullAddress(b)) *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "companyName") {
        return (
          (dspCompanyName(a) ?? "").localeCompare(dspCompanyName(b) ?? "") *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "old") {
        return (
          ((calcOld(a as MinimalEstate) ?? 0) -
            (calcOld(b as MinimalEstate) ?? 0)) *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "estatePrice") {
        return (
          (dspEstatePrice(a) - dspEstatePrice(b)) *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "tsuboArea") {
        return (
          (dspTsuboArea(a) - dspTsuboArea(b)) *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "estateType") {
        return (
          (a.estateType ?? "").localeCompare(b.estateType ?? "") *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "layout") {
        return (
          (layoutForCompare(a) - layoutForCompare(b)) *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "updatedAt") {
        return (
          updateAtForCompare(a).localeCompare(updateAtForCompare(b)) *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "sellingDays") {
        return (
          ((sellingDaysForCompare(a) ?? 0) - (sellingDaysForCompare(b) ?? 0)) *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      return 0;
    });
  }, [props.allHouseEstates, sortColumn, sortDirection]);

  return (
    <Accordion className={styles.accordion}>
      <AccordionSummary
        expandIcon={<ArrowDropDownIcon className={styles.icon} />}
        aria-controls="house-content"
        id="house-header"
      >
        <Box className={`${styles.category} ${styles.small}`}>
          <Box
            component="img"
            src={HouseIcon}
            alt={"戸建"}
            className={styles.estateTypeIcon}
          />
          戸建の過去事例（参考）：
          {sortedAllHouseEstates.length} 件
        </Box>
      </AccordionSummary>
      <AccordionDetails className={styles.details}>
        {sortedAllHouseEstates.length > 0 ? (
          <TableContainer className={styles.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <SortableColumn
                      column="status"
                      label="ステータス"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSortClick={onSortClick}
                    />
                  </TableCell>
                  <TableCell>
                    <SortableColumn
                      column="fullAddress"
                      label="所在地"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSortClick={onSortClick}
                    />
                  </TableCell>
                  <TableCell>
                    <SortableColumn
                      column="companyName"
                      label="業者名"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSortClick={onSortClick}
                    />
                  </TableCell>
                  <TableCell className={styles.alignRight}>
                    <SortableColumn
                      column="old"
                      label="築年数"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSortClick={onSortClick}
                    />
                  </TableCell>
                  <TableCell className={styles.alignRight}>
                    <SortableColumn
                      column="estatePrice"
                      label="価格"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSortClick={onSortClick}
                    />
                  </TableCell>
                  <TableCell className={styles.alignRight}>
                    <SortableColumn
                      column="tsuboArea"
                      label="広さ"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSortClick={onSortClick}
                    />
                  </TableCell>
                  <TableCell>
                    <SortableColumn
                      column="estateType"
                      label="種別"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSortClick={onSortClick}
                    />
                  </TableCell>
                  <TableCell>
                    <SortableColumn
                      column="layout"
                      label="間取り"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSortClick={onSortClick}
                    />
                  </TableCell>
                  <TableCell>
                    <SortableColumn
                      column="updatedAt"
                      label="掲載終了"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSortClick={onSortClick}
                    />
                  </TableCell>
                  <TableCell className={styles.alignRight}>
                    <SortableColumn
                      column="sellingDays"
                      label="売出日数"
                      currentSortColumn={sortColumn}
                      currentSortDirection={sortDirection}
                      onSortClick={onSortClick}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAllHouseEstates.map((estate) => {
                  return (
                    <TableRow key={estate.id}>
                      <TableCell>
                        <Chip
                          label={dspStatus(estate.status)}
                          className={
                            dspStatus(estate.status) === "売出中"
                              ? styles.publicStatusLabel
                              : styles.soldStatusLabel
                          }
                        />
                      </TableCell>
                      <TableCell>{dspFullAddress(estate)}</TableCell>
                      <TableCell>{dspCompanyName(estate)}</TableCell>
                      <TableCell className={styles.alignRight}>
                        {calcOld(estate as MinimalEstate) ?? "?"} 年
                      </TableCell>
                      <TableCell className={styles.alignRight}>
                        {String(dspEstatePrice(estate))} 万
                      </TableCell>
                      <TableCell className={styles.alignRight}>
                        {String(dspTsuboArea(estate))} 坪
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={estate.estateType}
                          className={styles.estateTypeLabel}
                        />
                      </TableCell>
                      <TableCell className={styles.layoutCell}>
                        {dspLayout(estate)}
                      </TableCell>
                      <TableCell>{dspUpdatedAt(estate)}</TableCell>
                      <TableCell className={styles.alignRight}>
                        {dspSellingDays(estate)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box className={styles.noData}>※ 過去事例がありませんでした。</Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
});
